import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      if (!token) {
        setCurrentUser(null);
        setPermissions({});
        setLoading(false);
        return;
      }

      const res = await axios.get('/me');
      const user = res.data?.user || null;
      setCurrentUser(user);

      const userPermissions = user?.role?.permissions_grouped || {};
      const roleName = (user?.role?.name || '').toLowerCase().trim();

      // If user is Admin / Super Admin, set 'all' flag
      if (roleName === 'it admin' || roleName === 'super admin' || roleName === 'admin') {
        userPermissions.all = true;
      }

      setPermissions(userPermissions);
    } catch (err) {
      console.warn('Unable to load permissions:', err);
      // Keep empty if unauthenticated or failed
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const userRole = currentUser?.role?.name || '';
  const isSuperAdmin = (
    permissions.all === true ||
    ['it admin', 'super admin', 'admin'].includes(userRole.toLowerCase().trim())
  );

  /**
   * Check if user has permission on a given module and action
   * @param {string} module - Module name (e.g. 'assets', 'users', 'contracts')
   * @param {string} action - Action verb (e.g. 'read', 'create', 'update', 'delete', 'export')
   */
  const hasPermission = useCallback((module, action = 'read') => {
    if (isSuperAdmin) return true;
    if (!permissions || !module) return false;

    const modulePerms = permissions[module];
    if (Array.isArray(modulePerms)) {
      return modulePerms.includes(action);
    }

    return false;
  }, [isSuperAdmin, permissions]);

  /**
   * Check if user has one of the specified roles
   * @param {string|string[]} roles - Single role or array of roles
   */
  const hasRole = useCallback((roles) => {
    if (!userRole) return false;
    if (isSuperAdmin) return true;

    const roleList = Array.isArray(roles) ? roles : [roles];
    return roleList.some(r => r.toLowerCase().trim() === userRole.toLowerCase().trim());
  }, [isSuperAdmin, userRole]);

  const value = {
    currentUser,
    userRole,
    permissions,
    isSuperAdmin,
    loading,
    hasPermission,
    hasRole,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Component to conditionally render content based on permissions
 */
export const PermissionGate = ({ module, action = 'read', fallback = null, children }) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) return null;
  if (!hasPermission(module, action)) {
    return fallback;
  }

  return <>{children}</>;
};

export default PermissionContext;

